
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Lock, CreditCard } from "lucide-react";

interface PaymentFormProps {
  coursePrice: number;
  loading: boolean;
}

const PaymentForm = ({ coursePrice, loading }: PaymentFormProps) => {
  const navigate = useNavigate();
  
  // States for form handling
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!name.trim()) newErrors.name = "Nimi on kohustuslik";
    if (!email.trim()) newErrors.email = "E-post on kohustuslik";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Palun sisesta kehtiv e-posti aadress";
    
    if (!cardNumber.trim()) newErrors.cardNumber = "Kaardi number on kohustuslik";
    else if (!/^\d{16}$/.test(cardNumber.replace(/\s/g, ''))) newErrors.cardNumber = "Palun sisesta kehtiv 16-kohaline kaardi number";
    
    if (!expiryDate.trim()) newErrors.expiryDate = "Aegumiskuupäev on kohustuslik";
    else if (!/^\d{2}\/\d{2}$/.test(expiryDate)) newErrors.expiryDate = "Palun sisesta kujul KK/AA";
    
    if (!cvv.trim()) newErrors.cvv = "CVV on kohustuslik";
    else if (!/^\d{3,4}$/.test(cvv)) newErrors.cvv = "Palun sisesta kehtiv CVV (3-4 numbrit)";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Simulate payment processing
      setTimeout(() => {
        // Redirect to success page on successful payment
        navigate("/checkout-success");
      }, 1500);
    }
  };
  
  // Format card number with spaces
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\s/g, '').substring(0, 16);
    const formatted = input.replace(/(\d{4})/g, '$1 ').trim();
    setCardNumber(formatted);
  };
  
  // Format expiry date
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, '').substring(0, 4);
    if (input.length > 2) {
      setExpiryDate(`${input.substring(0, 2)}/${input.substring(2)}`);
    } else {
      setExpiryDate(input);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Isiklikud andmed</h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Täisnimi</Label>
              <Input 
                id="name" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Sinu ees- ja perekonnanimi"
                className={errors.name ? "border-red-500" : ""}
              />
              {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">E-posti aadress</Label>
              <Input 
                id="email" 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="sinu.email@example.com"
                className={errors.email ? "border-red-500" : ""}
              />
              {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
            </div>
          </div>
        </div>
        
        <Separator />
        
        <div className="space-y-4">
          <div className="flex items-center">
            <h3 className="text-lg font-medium">Makseandmed</h3>
            <div className="ml-auto flex items-center text-sm text-gray-500">
              <Lock className="h-4 w-4 mr-1" />
              Turvaline makse
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cardNumber">Kaardi number</Label>
              <div className="relative">
                <Input 
                  id="cardNumber" 
                  value={cardNumber} 
                  onChange={handleCardNumberChange} 
                  placeholder="1234 5678 9012 3456"
                  className={`pl-10 ${errors.cardNumber ? "border-red-500" : ""}`}
                />
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              </div>
              {errors.cardNumber && <p className="text-red-500 text-sm">{errors.cardNumber}</p>}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Kehtiv kuni (KK/AA)</Label>
                <Input 
                  id="expiryDate" 
                  value={expiryDate} 
                  onChange={handleExpiryDateChange}
                  placeholder="MM/YY"
                  className={errors.expiryDate ? "border-red-500" : ""}
                />
                {errors.expiryDate && <p className="text-red-500 text-sm">{errors.expiryDate}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cvv">CVV</Label>
                <Input 
                  id="cvv" 
                  value={cvv} 
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').substring(0, 4))} 
                  placeholder="123"
                  className={errors.cvv ? "border-red-500" : ""}
                />
                {errors.cvv && <p className="text-red-500 text-sm">{errors.cvv}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-8">
        <Button 
          type="submit" 
          className="w-full bg-kaija-accent hover:bg-opacity-90 text-white py-6"
          disabled={loading}
        >
          {loading ? "Töötleme makset..." : `Maksa ${coursePrice}€`}
        </Button>
      </div>
    </form>
  );
};

export default PaymentForm;
